import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { pixelToCourt } from "../../../inJoy-components/3DHeapMap/courtUtils";
import { processRawShots } from "../../../inJoy-components/3DHeapMap/shotStatsUtils";

export interface TeamStats {
	made: number;
	total: number;
	score: number;
	made2: number;
	total2: number;
	made3: number;
	total3: number;
}

export interface KioskStats {
	rawShots: any[];
	shots: any[];
	teamStats: [TeamStats, TeamStats];
	teamScores: { team0: number; team1: number };
	getDetailedTeamStats: (teamId: 0 | 1) => {
		icon: ReactNode;
		name: string;
		score: number;
		isWinner: boolean;
		fg: { value: number; label: string };
		fg2: { value: number; label: string };
		ft: { value: number; label: string };
	};
	getTeamShots: (teamId: 0 | 1) => any[];
}

export const useKioskStats = (
	event: any,
	activeSessionIdx: number,
	teamIcons?: [ReactNode, ReactNode],
	teamNames?: [string, string],
): KioskStats => {
	const rawShots = useMemo(() => {
		if (event?.sessions?.[activeSessionIdx]) {
			return event.sessions[activeSessionIdx].result_details || [];
		}
		return [];
	}, [event, activeSessionIdx]);

	const shots = useMemo(() => {
		if (!rawShots || rawShots.length === 0) return [];

		return processRawShots(rawShots, pixelToCourt, {
			minHomographyConfidence: 0.0,
			_resolution: [1920, 1080],
		});
	}, [rawShots]);

	const teamStats = useMemo(() => {
		const stats: [TeamStats, TeamStats] = [
			{ made: 0, total: 0, score: 0, made2: 0, total2: 0, made3: 0, total3: 0 },
			{ made: 0, total: 0, score: 0, made2: 0, total2: 0, made3: 0, total3: 0 },
		];

		rawShots.forEach((result: any) => {
			const teamIdx = Number(result.team_id) === 0 ? 0 : 1;
			const isScore = result.field_goal_result === "score";
			const is3P = result.field_goal_position === "3PFG";

			stats[teamIdx].total++;
			if (is3P) {
				stats[teamIdx].total3++;
				if (isScore) {
					stats[teamIdx].made++;
					stats[teamIdx].made3++;
					stats[teamIdx].score += 3;
				}
			} else {
				stats[teamIdx].total2++;
				if (isScore) {
					stats[teamIdx].made++;
					stats[teamIdx].made2++;
					stats[teamIdx].score += 2;
				}
			}
		});

		return stats;
	}, [rawShots]);

	const teamScores = useMemo(
		() => ({
			team0: teamStats[0].score,
			team1: teamStats[1].score,
		}),
		[teamStats],
	);

	const getDetailedTeamStats = useCallback(
		(teamId: 0 | 1) => {
			const stats = teamStats[teamId];
			const teamIcon = teamIcons?.[teamId] ?? null;
			const teamName =
				teamNames?.[teamId] ?? (teamId === 0 ? "Team A" : "Team B");
			const otherTeamScore = teamId === 0 ? teamScores.team1 : teamScores.team0;

			const fgPct =
				stats.total > 0 ? Math.round((stats.made / stats.total) * 100) : 0;
			const p2Pct =
				stats.total2 > 0 ? Math.round((stats.made2 / stats.total2) * 100) : 0;
			const p3Pct =
				stats.total3 > 0 ? Math.round((stats.made3 / stats.total3) * 100) : 0;

			return {
				icon: teamIcon,
				name: teamName,
				score: stats.score,
				isWinner: stats.score > otherTeamScore,
				fg: { value: fgPct, label: "FG%" },
				fg2: { value: p2Pct, label: "2P%" },
				ft: { value: p3Pct, label: "3P%" },
			};
		},
		[teamStats, teamScores, teamIcons, teamNames],
	);

	const getTeamShots = useCallback(
		(teamId: 0 | 1) => {
			return shots.filter((s: any) => s.team_id === teamId);
		},
		[shots],
	);

	return {
		rawShots,
		shots,
		teamStats,
		teamScores,
		getDetailedTeamStats,
		getTeamShots,
	};
};
